import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GetLinkPrefetchReportDocument, GetLinkPrefetchReportQuery } from '../generated/graphql';
import { DateTime } from 'luxon';

type LinkPrefetchReportMetrics = {
  pv: number;
  dcl: number;
  lcp: number;
  cls: number;
};

type LinkPrefetchReportDaily = {
  date: string;
  control: LinkPrefetchReportMetrics;
  experimental: LinkPrefetchReportMetrics;
};

type LinkPrefetchReportPerPattern = {
  pv: number;
  control: LinkPrefetchReportMetrics;
  experimental: LinkPrefetchReportMetrics;
  dailies: LinkPrefetchReportDaily[];
};

// 加工後のチャートデータの型定義
export type LinkPrefetchReportData = {
  patterns: { [pattern: string]: LinkPrefetchReportPerPattern };
};

export type LinkPrefetchReportThreshold = {
  pv: number; // この PV 以下は「データ不足」扱いとする
  lcpWarn: number; // LCP の「改善が必要」の閾値
  lcpPoor: number; // LCP の「低速」の閾値
  clsWarn: number; // CLS の「改善が必要」の閾値
  clsPoor: number; // CLS の「低速」の閾値
};

export const DefaultLinkPrefetchReportThreshold: LinkPrefetchReportThreshold = {
  pv: 1000,
  lcpWarn: 2.5,
  lcpPoor: 4.0,
  clsWarn: 0.1,
  clsPoor: 0.25,
};

export type MetricCategory = 0 | 1 | 2;
export const MetricCategoryLabel: { [category in MetricCategory]: string } = {
  0: '良好',
  1: '改善が必要',
  2: '低速',
};

export function classifyMetric(rawValue: number, warnThreshold: number, poorThreshold: number): MetricCategory {
  const value = rawValue / 1000;
  if (value < warnThreshold) return 0;
  if (value < poorThreshold) return 1;
  return 2;
}

type LinkPrefetchReportTableColumns = 'date' | 'pattern' | 'pv' | 'avg_dcl' | 'avg_lcp' | 'avg_cls' | 'control_group';

type LinkPrefetchReportRawData = {
  chart: {
    table: {
      columns: LinkPrefetchReportTableColumns[];
      rows: string[][];
    };
  };
};

function convertRawDataToPatterns(
  raw: LinkPrefetchReportRawData,
  fromDate: DateTime,
  toDate: DateTime,
): { [pattern: string]: LinkPrefetchReportPerPattern } {
  const { columns, rows } = raw.chart.table;

  const dateIndex = columns.indexOf('date');
  const patternIndex = columns.indexOf('pattern');
  const pvIndex = columns.indexOf('pv');
  const avgDclIndex = columns.indexOf('avg_dcl');
  const avgLcpIndex = columns.indexOf('avg_lcp');
  const avgClsIndex = columns.indexOf('avg_cls');
  const controlGroupIndex = columns.indexOf('control_group');

  const patterns: { [pattern: string]: LinkPrefetchReportPerPattern } = {};
  const zeroMetrics = { pv: 0, dcl: 0, lcp: 0, cls: 0 };
  const forEachDate = (callback: (date: string) => void) => {
    for (let date = fromDate; toDate.diff(date, 'days').days >= 0; date = date.plus({ days: 1 })) {
      callback(date.toFormat('yyyy-MM-dd'));
    }
  };

  rows.forEach((row) => {
    const pattern = row[patternIndex];
    const date = row[dateIndex];
    const pv = parseInt(row[pvIndex], 10);
    const dcl = parseFloat(row[avgDclIndex]);
    const lcp = parseFloat(row[avgLcpIndex]);
    const cls = parseFloat(row[avgClsIndex]);
    const controlGroup = row[controlGroupIndex];

    if (!patterns[pattern]) {
      patterns[pattern] = {
        pv: 0,
        control: zeroMetrics,
        experimental: zeroMetrics,
        dailies: [],
      };
    }

    let daily = patterns[pattern].dailies.find((d) => d.date === date);
    if (!daily) {
      daily = {
        date,
        control: zeroMetrics,
        experimental: zeroMetrics,
      };
      patterns[pattern].dailies.push(daily);
    }
    if (controlGroup === '1') {
      daily.control = { pv, dcl, lcp, cls };
    } else {
      daily.experimental = { pv, dcl, lcp, cls };
    }
  });

  Object.values(patterns).forEach((pattern) => {
    // 日付でソートして抜けている日付を 0 で補完
    let dailies: LinkPrefetchReportDaily[] = [];
    let totalControl = { pv: 0, dcl: 0, lcp: 0, cls: 0 };
    let totalExperimental = { pv: 0, dcl: 0, lcp: 0, cls: 0 };
    forEachDate((date) => {
      let daily = pattern.dailies.find((d) => d.date === date);
      if (daily) {
        totalControl = {
          pv: totalControl.pv + daily.control.pv,
          dcl: totalControl.dcl + daily.control.dcl * daily.control.pv,
          lcp: totalControl.lcp + daily.control.lcp * daily.control.pv,
          cls: totalControl.cls + daily.control.cls * daily.control.pv,
        };
        totalExperimental = {
          pv: totalExperimental.pv + daily.experimental.pv,
          dcl: totalExperimental.dcl + daily.experimental.dcl * daily.experimental.pv,
          lcp: totalExperimental.lcp + daily.experimental.lcp * daily.experimental.pv,
          cls: totalExperimental.cls + daily.experimental.cls * daily.experimental.pv,
        };
      } else {
        daily = {
          date,
          control: zeroMetrics,
          experimental: zeroMetrics,
        };
      }
      dailies.push(daily);
    });
    pattern.dailies = dailies;

    totalControl.dcl = totalControl.pv > 0 ? totalControl.dcl / totalControl.pv : 0;
    totalControl.lcp = totalControl.pv > 0 ? totalControl.lcp / totalControl.pv : 0;
    totalControl.cls = totalControl.pv > 0 ? totalControl.cls / totalControl.pv : 0;
    totalExperimental.dcl = totalExperimental.pv > 0 ? totalExperimental.dcl / totalExperimental.pv : 0;
    totalExperimental.lcp = totalExperimental.pv > 0 ? totalExperimental.lcp / totalExperimental.pv : 0;
    totalExperimental.cls = totalExperimental.pv > 0 ? totalExperimental.cls / totalExperimental.pv : 0;
    pattern.pv = totalControl.pv + totalExperimental.pv;
    pattern.control = totalControl;
    pattern.experimental = totalExperimental;
  });

  return patterns;
}

export const useLinkPrefetchReport = (organizationId: string, fromDate: DateTime, toDate: DateTime) => {
  const scope = { scope: { from: fromDate.toFormat('yyyy-MM-dd'), to: toDate.toFormat('yyyy-MM-dd') } };
  const { data, error, loading } = useQuery<GetLinkPrefetchReportQuery>(GetLinkPrefetchReportDocument, {
    context: { clientName: 'dataplatform' },
    fetchPolicy: 'cache-first',
    variables: {
      id: `planbcd_speculation_rule_performance_${organizationId}`,
      identifierType: 'resource_key',
      breakdowns: [{ breakdown: 'control_group', filter: ['0', '1'] }],
      ...scope,
    },
  });

  // useMemo を使って、取得したデータをチャート用の形式に加工する
  const reportData = useMemo<LinkPrefetchReportData | null>(() => {
    if (!data?.chart?.table?.columns && !data?.chart?.table?.rows) {
      return null;
    }

    return { patterns: convertRawDataToPatterns(data as LinkPrefetchReportRawData, fromDate, toDate) };
  }, [data]);

  return { error, loading, reportData };
};
